import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import type { InboundPendingSender, InboundStatus, PurchaseInvoiceSummary } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { useToast } from '../components/Toast';
import { formatDate, formatEur } from '../lib/format';
import { Button, Card, Chip, Empty, Loading, PageHeader, purchaseInvoiceStatusLabel, purchaseInvoiceTone } from '../components/ui';

const MAX_FILES = 20;
const FILTERS = ['all', 'pending', 'duplicate', 'booked', 'ignored'] as const;

interface UploadResult { filename: string; ok: boolean; error?: string; row?: PurchaseInvoiceSummary }

export function Dossier() {
  const { company } = useAuth();
  const { t, s, lang } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'all';

  const q = useQuery({
    queryKey: ['purchase-invoices', company?.id, status],
    queryFn: () => capi<PurchaseInvoiceSummary[]>(`/purchase-invoices?status=${status}&limit=300`),
  });

  const upload = useMutation({
    mutationFn: (files: File[]) => {
      const fd = new FormData();
      for (const f of files) fd.append('files', f, f.name);
      return capi<UploadResult[]>('/purchase-invoices', { method: 'POST', body: fd });
    },
    onSuccess: (results) => {
      qc.invalidateQueries({ queryKey: ['purchase-invoices'] });
      const ok = results.filter((r) => r.ok).length;
      if (ok) toast.push(t('dossierUploadDone', { ok, count: results.length }));
      for (const r of results.filter((r) => !r.ok)) toast.push(t('dossierUploadFailed', { name: r.filename, error: r.error ?? '' }), 'error');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const pick = (files: File[]) => {
    const pdfs = files.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')).slice(0, MAX_FILES);
    if (pdfs.length) upload.mutate(pdfs);
  };

  return (
    <>
      <PageHeader crumb={t('dossierCrumb')} title={t('dossierTitle')} sub={t('dossierSub')}
        actions={<>
          <Button icon="fas fa-triangle-exclamation" onClick={() => navigate('/dossier/gaps')}>{t('dossierGaps')}</Button>
          <Button icon="fas fa-file-zipper" title={t('dossierExportSub')} onClick={() => window.open(`/api/companies/${company?.id}/dossier/export`, '_blank')}>{t('btnExport')}</Button>
          <Button variant="primary" icon="fas fa-file-arrow-up" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
            {upload.isPending ? t('importing') : t('dossierUpload')}
          </Button>
        </>} />
      <input ref={fileInput} type="file" accept="application/pdf" multiple hidden onChange={(e) => { pick(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
      <div className="stack">
        <MailboxBanner />
        <div className="row">
          {FILTERS.map((f) => (
            <button key={f} type="button" className={`pill ${status === f ? 'active' : ''}`} style={{ background: status === f ? undefined : 'var(--btn-bg)', borderColor: status === f ? undefined : 'var(--glass-border)' }}
              onClick={() => setParams((p) => { p.set('status', f); return p; })}>
              {f === 'all' ? s('All') : s(purchaseInvoiceStatusLabel(f))}
            </button>
          ))}
          <div className="spacer" />
          {q.data && <span className="small muted">{q.data.length}</span>}
        </div>
        <Card pad={false} className="table">
          {!q.data ? <Loading /> : (
            <table>
              <thead><tr><th>{t('colIssued')}</th><th>{t('colSupplier')}</th><th>{t('invoiceNumber')}</th><th>{t('colBoekstuk')}</th><th>{t('colStatus')}</th><th className="r">{t('colAmount')}</th></tr></thead>
              <tbody>
                {q.data.length === 0 && <Empty />}
                {q.data.map((r) => (
                  <tr key={r.id} className="click" onClick={() => navigate(`/dossier/${r.id}`)}>
                    <td className="secondary small">{r.date ? formatDate(r.date, lang) : '—'}</td>
                    <td style={{ fontWeight: 600 }}>{r.relationName || <span className="muted">{s('Unrecognised')}</span>}</td>
                    <td className="ref">{r.number ?? '—'}</td>
                    <td className="secondary small mono">{r.ebEntryNumber ?? '—'}</td>
                    <td><Chip tone={purchaseInvoiceTone(r.status)}>{s(purchaseInvoiceStatusLabel(r.status))}</Chip></td>
                    <td className="amount">{formatEur(r.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}

/** The invoice mailbox in one line: where suppliers mail to, and who waits for approval. */
function MailboxBanner() {
  const { company } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const status = useQuery({ queryKey: ['inbound-status', company?.id], queryFn: () => capi<InboundStatus>('/inbound') });
  const pending = useQuery({ queryKey: ['inbound-pending', company?.id], queryFn: () => capi<InboundPendingSender[]>('/inbound/pending'), enabled: !!status.data?.enabled });
  if (!status.data?.enabled) return null;
  const n = pending.data?.length ?? 0;
  return (
    <div className={`banner ${n ? 'tint-warning' : 'tint-accent'} row`}>
      <i className="fas fa-inbox" aria-hidden="true" />
      <span style={{ flex: 1 }}>{t('mbBanner', { address: status.data.address ?? '' })}{n ? ` ${t('mbBannerPending', { n })}` : ''}</span>
      <Button size="sm" icon="fas fa-arrow-right" onClick={() => navigate('/mailbox')}>{t('mbOpen')}</Button>
    </div>
  );
}
